import React, { useState, useEffect } from "react";
import { apiurl, staticurl } from "../common/constants";
import { useAuthAxios } from "./authfetch";

import { CompactTable } from "@table-library/react-table-library/compact";
import { useTheme } from "@table-library/react-table-library/theme";
import { getTheme } from "@table-library/react-table-library/baseline";
import useSessionStorage from "../hooks/useSessionStorage";
import { useStore } from "../context/StoreContext";

const OrderRowDetails = ({ item, onUpdate }) => {
  const [notes, setNotes] = useState(item.notes || "");
  const [status, setStatus] = useState(item.status || "Pending");

  useEffect(() => {
    setNotes(item.notes || "");
    setStatus(item.status || "Pending");
  }, [item]);

  const handleSave = () => {
    onUpdate(item._id, status, notes);
  };

  return (
    <div className="p-3 bg-light border-bottom">
      <div className="row">
        <div className="col-md-6">
          <h6 className="fw-bold">Customer Details</h6>
          <ul className="list-unstyled">
            <li>
              <strong>Name:</strong> {item.user?.name}
            </li>
            <li>
              <strong>Email:</strong> {item.user?.email}
            </li>
            <li>
              <strong>Phone:</strong> {item.phoneNumber}
            </li>
            <li>
              <strong>Full Address:</strong> {item.address}
            </li>
          </ul>
        </div>
        <div className="col-md-6">
          <h6 className="fw-bold">Order Details</h6>
          <ul className="list-unstyled">
            <li>
              <strong>Order ID:</strong> {item._id}
            </li>
            <li>
              <strong>Status:</strong> {item.status}
            </li>
            <li>
              <strong>Notes:</strong> <pre>{item.notes}</pre>
            </li>
            <li>
              <strong>Ordered On:</strong>{" "}
              {new Date(item.createdAt).toLocaleString()}
            </li>
            <li>
              <strong>Product:</strong> 
              <a target="_blank" href={"/product/" + item.product._id}>{item.product?.prod_name}</a>
            </li>
            <li>
              <strong>Amount:</strong> Rs. {item.amount}
            </li>
            <li>
              <strong>Pre Order:</strong> {item.pre_order ? "Yes" : "No"}
            </li>
            {item.product?.prod_thumbnail && (
              <li className="mt-2">
                <img
                  loading="lazy"
                  src={staticurl + item.product.prod_thumbnail}
                  alt="thumbnail"
                  style={{
                    height: "60px",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                  }}
                />
              </li>
            )}
          </ul>
        </div>
      </div>
      <div className="mt-3 pt-3 border-top">
        <h6 className="fw-bold">Actions</h6>
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label small">Update Status</label>
            <select
              className="form-select form-select-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="Pending">Pending</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div className="col-md-8">
            <label className="form-label small">Order Notes</label>
            <div className="input-group input-group-sm">
              <textarea
                className="form-control"
                rows="1"
                placeholder="Add internal notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              ></textarea>
              <button
                className="btn btn-primary"
                type="button"
                onClick={handleSave}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function OrderViewer() {
  const [orders, setOrders] = useState([]);
  const {setIsLoading, isLoading} = useStore();
  const authAxios = useAuthAxios();

  // State for expanded rows
  const [ids, setIds] = useState([]);

  const [filterStatus, setFilterStatus] = useSessionStorage("orderFilterStatus", ["Pending", "Completed", "Cancelled"]);

  const handleFilterChange = (status) => {
    if (filterStatus.includes(status)) {
      setFilterStatus(filterStatus.filter((s) => s !== status));
    } else {
      setFilterStatus([...filterStatus, status]);
    }
  };

  const handleUpdateOrder = async (orderId, newStatus, newNotes) => {
    try {
      const { data } = await authAxios.put(apiurl + "/orders/" + orderId, {
        notes: newNotes,
        status: newStatus
      });
      
      if (data.success && data.data?.order) {
        // Update local state without losing populated fields
        setOrders(prevOrders => prevOrders.map(o => {
            if (o._id === data.data.order._id) {
                return {
                    ...o,
                    status: data.data.order.status,
                    notes: data.data.order.notes,
                    updatedAt: data.data.order.updatedAt
                };
            }
            return o;
        }));
        alert("Order updated successfully");
      } else {
        alert("Failed to update order");
      }
    } catch (error) {
      console.error("Update failed", error);
      alert("Error updating order");
    }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      try {
        const { data } = await authAxios.get(apiurl + "/orders");
        if (data.success && data.data && Array.isArray(data.data.orders)) {
          setOrders(data.data.orders);
        } else {
          console.warn("Unexpected orders data format", data);
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const handleExpand = (item) => {
    // colapse if alredy expanded
    if (ids.includes(item.id)) {
      setIds([]);
      return;
    }
    const idx = [item.id];
    setIds(idx);
  };

  const filteredOrders = orders.filter((order) => filterStatus.includes(order.status));

  const data = {
    nodes: filteredOrders.map((order) => ({
      ...order,
      id: order._id,
    })),
  };

  const theme = useTheme([
    getTheme(),
    {
      Table: `
        --data-table-library_grid-template-columns: 100px 150px 200px 150px 200px 150px 100px 100px;
        cursor: pointer; // Indicate clickable rows
      `,
    },
  ]);
  const statusClasses = {
    completed: "bg-success",
    pending: "bg-warning text-dark",
    cancelled: "bg-danger",
    default: "bg-secondary"
  };
  const COLUMNS = [
    { label: "Order ID", renderCell: (item) => <small>{item._id}</small> },
    { label: "Name", renderCell: (item) => item.user?.name || "N/A" },
    { label: "Email", renderCell: (item) => item.user?.email || "N/A" },
    { label: "Phone", renderCell: (item) => item.phoneNumber },
    {
      label: "Address",
      renderCell: (item) => (
        <span className="text-truncate d-block" style={{ maxWidth: "100%" }}>
          {item.address}
        </span>
      ),
    },
    {
      label: "Product",
      renderCell: (item) => item.product?.prod_name || "N/A",
    },
    {
      label: "Status",
      renderCell: (item) => (
        <span
          className={`badge ${statusClasses[item.status.toLowerCase()] || statusClasses.default}`}
        >
          {item.status}
        </span>
      ),
    },
    {
      label: "Date",
      renderCell: (item) =>
        item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "N/A",
    },
  ];

  const ROW_PROPS = {
    onClick: handleExpand,
  };

  const ROW_OPTIONS = {
    renderAfterRow: (item) => (
      <>
        {ids.includes(item.id) && (
          <tr style={{ display: "flex", gridColumn: "1 / -1" }}>
            <td style={{ flex: "1" }}>
               <OrderRowDetails item={item} onUpdate={handleUpdateOrder} />
            </td>
          </tr>
        )}
      </>
    ),
  };

  if (isLoading) return <div className="p-3">Loading Orders...</div>;

  return (
    <div className="mt-3">
      <h3>Orders</h3>
      
      <div className="d-flex flex-column flex-md-row gap-2 gap-md-3 my-3 align-items-start align-items-md-center">
        <div className="fw-bold">Filter By Status:</div>
        <div className="d-flex flex-wrap gap-2">
          {["Pending", "Completed", "Cancelled"].map((status) => (
            <div className="form-check" key={status}>
              <input
                className="form-check-input"
                type="checkbox"
                value={status}
                id={`filter-${status}`}
                checked={filterStatus.includes(status)}
                onChange={() => handleFilterChange(status)}
              />
              <label className="form-check-label" htmlFor={`filter-${status}`}>
                {status}
              </label>
            </div>
          ))}
        </div>
      </div>

      <p className="text-muted small">Click on a row to view details.</p>
      {filteredOrders.length === 0 ? (
        <p>No orders found matching the selected filters.</p>
      ) : (
        <div
          style={{
            padding: "20px",
            backgroundColor: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <CompactTable
            columns={COLUMNS}
            data={data}
            theme={theme}
            rowProps={ROW_PROPS}
            rowOptions={ROW_OPTIONS}
            layout={{ custom: true, horizontalScroll: true }}
          />
        </div>
      )}
    </div>
  );
}
